import { useState, useEffect, useCallback } from "react";
import { api } from "@/App";
import {
  Package, Search, ExternalLink, ShoppingCart, Plus, Trash2, Save,
  Settings2, History, CheckCircle2, Truck, Star, X, Puzzle, Copy, Download, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/App";

const DISPO_OPTIONS = [
  { value: "stock", label: "En stock", color: "bg-emerald-100 text-emerald-700" },
  { value: "commande", label: "Sur commande", color: "bg-amber-100 text-amber-700" },
  { value: "rupture", label: "Rupture", color: "bg-red-100 text-red-700" },
  { value: "-", label: "Non renseigné", color: "bg-slate-100 text-slate-500" },
];

const parsePrix = (s) => {
  if (!s) return NaN;
  const n = parseFloat(String(s).replace(/[^\d.,]/g, "").replace(",", "."));
  return isNaN(n) ? NaN : n;
};

const buildSearchUrl = (tpl, ref) => {
  if (!tpl) return "";
  const enc = encodeURIComponent(ref || "");
  return tpl.includes("{ref}") ? tpl.replace(/\{ref\}/g, enc) : tpl;
};

export default function PiecesView() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [suppliers, setSuppliers] = useState([]);
  const [history, setHistory] = useState([]);
  const [cart, setCart] = useState([]);

  const [reference, setReference] = useState("");
  const [designation, setDesignation] = useState("");
  const [vehicleLabel, setVehicleLabel] = useState("");
  const [activeSearch, setActiveSearch] = useState(null);
  const [quotes, setQuotes] = useState({}); // supplier_id -> {prix,dispo,delai,note}
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("compare");

  const [supplierDialog, setSupplierDialog] = useState(false);
  const [editSupplier, setEditSupplier] = useState(null);
  const [supplierForm, setSupplierForm] = useState({ name: "", search_url: "", domain: "", color: "#334155", order: 99, active: true });

  const [extDialog, setExtDialog] = useState(false);
  const [captureKey, setCaptureKey] = useState("");
  const backendBase = process.env.REACT_APP_BACKEND_URL || "";

  const fetchAll = useCallback(async () => {
    try {
      const [s, h, c] = await Promise.all([
        api.get("/parts/suppliers"),
        api.get("/parts/searches?limit=20"),
        api.get("/parts/cart"),
      ]);
      setSuppliers(s.data);
      setHistory(h.data);
      setCart(c.data);
    } catch (e) {
      toast.error("Erreur de chargement");
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const loadSearchIntoTable = (search) => {
    setActiveSearch(search);
    setReference(search.reference);
    setDesignation(search.designation || "");
    setVehicleLabel(search.vehicle_label || "");
    const q = {};
    (search.quotes || []).forEach((it) => {
      q[it.supplier_id] = { prix: it.prix || "", dispo: it.dispo || "", delai: it.delai || "", note: it.note || "" };
    });
    setQuotes(q);
  };

  const handleSearch = async () => {
    if (!reference.trim()) { toast.error("Saisissez une référence"); return; }
    try {
      const res = await api.post("/parts/searches", {
        reference: reference.trim(), designation, vehicle_label: vehicleLabel,
      });
      setActiveSearch(res.data);
      setQuotes({});
      fetchAll();
      toast.success("Recherche créée — ouvrez les sites puis notez les prix");
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const openAllSites = () => {
    const withUrl = suppliers.filter((s) => s.active && buildSearchUrl(s.search_url, reference));
    if (withUrl.length === 0) { toast.error("Aucun site avec URL configurée"); return; }
    withUrl.forEach((s) => window.open(buildSearchUrl(s.search_url, reference), "_blank", "noopener"));
  };

  const setQuoteField = (supplierId, field, value) => {
    setQuotes((prev) => ({ ...prev, [supplierId]: { ...(prev[supplierId] || {}), [field]: value } }));
  };

  const saveQuotes = async () => {
    if (!activeSearch) return;
    setSaving(true);
    try {
      const payload = suppliers.map((s) => ({
        supplier_id: s.id, supplier_name: s.name,
        prix: quotes[s.id]?.prix || "", dispo: quotes[s.id]?.dispo || "",
        delai: quotes[s.id]?.delai || "", note: quotes[s.id]?.note || "",
      }));
      await api.put(`/parts/searches/${activeSearch.id}`, { designation, vehicle_label: vehicleLabel, quotes: payload });
      toast.success("Comparatif enregistré");
      fetchAll();
    } catch (e) {
      toast.error("Erreur d'enregistrement");
    }
    setSaving(false);
  };

  const addToCart = async (supplier) => {
    if (!reference.trim()) { toast.error("Référence manquante"); return; }
    try {
      await api.post("/parts/cart", {
        reference: reference.trim(), designation, supplier_id: supplier.id,
        supplier_name: supplier.name, prix: quotes[supplier.id]?.prix || "",
        quantite: 1, vehicle_label: vehicleLabel, search_id: activeSearch?.id || "",
      });
      toast.success(`Ajouté au panier (${supplier.name})`);
      fetchAll();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const cheapestId = (() => {
    let best = null, bestVal = Infinity;
    suppliers.forEach((s) => {
      const v = parsePrix(quotes[s.id]?.prix);
      if (!isNaN(v) && quotes[s.id]?.dispo !== "rupture" && v < bestVal) { bestVal = v; best = s.id; }
    });
    return best;
  })();

  // ----- Cart -----
  const updateCartItem = async (id, updates) => {
    try {
      await api.put(`/parts/cart/${id}`, updates);
      fetchAll();
    } catch (e) { toast.error("Erreur"); }
  };
  const deleteCartItem = async (id) => {
    try { await api.delete(`/parts/cart/${id}`); fetchAll(); } catch (e) { toast.error("Erreur"); }
  };

  const cartBySupplier = cart.reduce((acc, it) => {
    const k = it.supplier_name || "Autre";
    (acc[k] = acc[k] || []).push(it);
    return acc;
  }, {});
  const cartTotal = cart.reduce((t, it) => {
    const v = parsePrix(it.prix); return t + (isNaN(v) ? 0 : v * (it.quantite || 1));
  }, 0);

  // ----- Supplier management -----
  const openSupplierDialog = (s) => {
    if (s) { setEditSupplier(s); setSupplierForm({ name: s.name, search_url: s.search_url || "", domain: s.domain || "", color: s.color || "#334155", order: s.order ?? 99, active: s.active !== false }); }
    else { setEditSupplier(null); setSupplierForm({ name: "", search_url: "", domain: "", color: "#334155", order: 99, active: true }); }
    setSupplierDialog(true);
  };
  const saveSupplier = async () => {
    if (!supplierForm.name.trim()) { toast.error("Nom requis"); return; }
    try {
      if (editSupplier) await api.put(`/parts/suppliers/${editSupplier.id}`, supplierForm);
      else await api.post("/parts/suppliers", supplierForm);
      toast.success("Fournisseur enregistré");
      setSupplierDialog(false);
      fetchAll();
    } catch (e) { toast.error("Erreur"); }
  };
  const deleteSupplier = async (id) => {
    if (!window.confirm("Supprimer ce fournisseur ?")) return;
    try { await api.delete(`/parts/suppliers/${id}`); fetchAll(); } catch (e) { toast.error("Erreur"); }
  };

  const openExtension = async () => {
    setExtDialog(true);
    if (!captureKey) {
      try {
        const res = await api.get("/parts/capture-key");
        setCaptureKey(res.data.key);
      } catch (e) { toast.error("Erreur clé de capture"); }
    }
  };
  const regenerateKey = async () => {
    if (!window.confirm("Régénérer la clé ? L'ancienne clé ne fonctionnera plus dans l'extension.")) return;
    try {
      const res = await api.post("/parts/capture-key/regenerate");
      setCaptureKey(res.data.key);
      toast.success("Nouvelle clé générée");
    } catch (e) { toast.error("Erreur"); }
  };
  const copyText = (txt, label) => {
    navigator.clipboard.writeText(txt);
    toast.success(`${label} copié`);
  };

  const dispoBadge = (val) => {
    const o = DISPO_OPTIONS.find((d) => d.value === val) || DISPO_OPTIONS[3];
    return <Badge className={o.color}>{o.label}</Badge>;
  };

  return (
    <div className="p-6 space-y-6 animate-fade-in" data-testid="pieces-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
            <Package className="h-8 w-8" />
            Commande de pièces
          </h1>
          <p className="text-slate-600 mt-1">Comparez prix &amp; dispo sur vos fournisseurs, puis constituez votre commande</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={openExtension} data-testid="extension-btn">
              <Puzzle className="h-4 w-4 mr-2" /> Extension Chrome
            </Button>
            <Button variant="outline" onClick={() => openSupplierDialog(null)} data-testid="add-supplier-btn">
              <Settings2 className="h-4 w-4 mr-2" /> Gérer les fournisseurs
            </Button>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList data-testid="pieces-tabs">
          <TabsTrigger value="compare" data-testid="tab-compare"><Search className="h-4 w-4 mr-2" />Comparateur</TabsTrigger>
          <TabsTrigger value="cart" data-testid="tab-cart">
            <ShoppingCart className="h-4 w-4 mr-2" />Panier
            {cart.length > 0 && <span className="ml-2 bg-red-600 text-white rounded-full px-2 text-xs">{cart.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history"><History className="h-4 w-4 mr-2" />Historique</TabsTrigger>
        </TabsList>

        {/* ---------- COMPARATEUR ---------- */}
        <TabsContent value="compare" className="space-y-6 mt-4">
          <Card className="border-slate-200">
            <CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Référence pièce *</Label>
                  <Input
                    data-testid="ref-input"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    placeholder="Ex: 8200551942"
                    className="font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Désignation (optionnel)</Label>
                  <Input data-testid="designation-input" value={designation} onChange={(e) => setDesignation(e.target.value)} placeholder="Plaquettes avant" />
                </div>
                <div className="space-y-2">
                  <Label>Véhicule / immat (optionnel)</Label>
                  <Input data-testid="vehicle-input" value={vehicleLabel} onChange={(e) => setVehicleLabel(e.target.value)} placeholder="Clio IV — AB-123-CD" />
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button onClick={handleSearch} className="bg-red-600 hover:bg-red-700" data-testid="search-btn">
                  <Search className="h-4 w-4 mr-2" /> Lancer la recherche
                </Button>
                <Button variant="outline" onClick={openAllSites} disabled={!reference.trim()} data-testid="open-all-btn">
                  <ExternalLink className="h-4 w-4 mr-2" /> Ouvrir tous les sites
                </Button>
              </div>
            </CardContent>
          </Card>

          {activeSearch && (
            <Card className="border-slate-200" data-testid="compare-table-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">
                  Comparatif — <span className="font-mono">{activeSearch.reference}</span>
                </CardTitle>
                <Button onClick={saveQuotes} disabled={saving} size="sm" data-testid="save-quotes-btn">
                  <Save className="h-4 w-4 mr-2" /> Enregistrer
                </Button>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Fournisseur</TableHead>
                      <TableHead className="w-28">Prix (€)</TableHead>
                      <TableHead className="w-40">Dispo</TableHead>
                      <TableHead className="w-28">Délai</TableHead>
                      <TableHead>Note</TableHead>
                      <TableHead className="w-44 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {suppliers.filter((s) => s.active !== false).map((s) => {
                      const url = buildSearchUrl(s.search_url, reference);
                      const isBest = s.id === cheapestId;
                      return (
                        <TableRow key={s.id} className={isBest ? "bg-emerald-50" : "hover:bg-slate-50"} data-testid={`compare-row-${s.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color || "#334155" }} />
                              <span className="font-semibold">{s.name}</span>
                              {isBest && <Star className="h-4 w-4 text-emerald-600 fill-emerald-500" title="Moins cher" />}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Input
                              data-testid={`prix-${s.id}`}
                              value={quotes[s.id]?.prix || ""}
                              onChange={(e) => setQuoteField(s.id, "prix", e.target.value)}
                              placeholder="0,00"
                              className="h-9"
                            />
                          </TableCell>
                          <TableCell>
                            <Select value={quotes[s.id]?.dispo || ""} onValueChange={(v) => setQuoteField(s.id, "dispo", v)}>
                              <SelectTrigger className="h-9" data-testid={`dispo-${s.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                              <SelectContent>
                                {DISPO_OPTIONS.filter((d) => d.value !== "-").map((d) => (
                                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input value={quotes[s.id]?.delai || ""} onChange={(e) => setQuoteField(s.id, "delai", e.target.value)} placeholder="24h" className="h-9" />
                          </TableCell>
                          <TableCell>
                            <Input value={quotes[s.id]?.note || ""} onChange={(e) => setQuoteField(s.id, "note", e.target.value)} placeholder="—" className="h-9" />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              {url ? (
                                <Button variant="ghost" size="icon" title="Ouvrir le site" onClick={() => window.open(url, "_blank", "noopener")} data-testid={`open-${s.id}`}>
                                  <ExternalLink className="h-4 w-4" />
                                </Button>
                              ) : (
                                <Button variant="ghost" size="icon" disabled title="Aucune URL configurée"><ExternalLink className="h-4 w-4 opacity-30" /></Button>
                              )}
                              <Button variant="ghost" size="icon" className="text-red-600" title="Ajouter au panier" onClick={() => addToCart(s)} data-testid={`addcart-${s.id}`}>
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ---------- PANIER ---------- */}
        <TabsContent value="cart" className="space-y-4 mt-4">
          {cart.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <ShoppingCart className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>Aucun article dans le panier</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="text-slate-600">{cart.length} article(s)</p>
                <p className="text-lg font-bold" data-testid="cart-total">Total estimé : {cartTotal.toFixed(2)} €</p>
              </div>
              {Object.entries(cartBySupplier).map(([sup, items]) => (
                <Card key={sup} className="border-slate-200">
                  <CardHeader className="py-3"><CardTitle className="text-base">{sup}</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Référence</TableHead>
                          <TableHead>Désignation</TableHead>
                          <TableHead>Véhicule</TableHead>
                          <TableHead className="w-20">Qté</TableHead>
                          <TableHead className="w-24">Prix</TableHead>
                          <TableHead className="w-40">Statut</TableHead>
                          <TableHead className="w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((it) => (
                          <TableRow key={it.id} data-testid={`cart-row-${it.id}`}>
                            <TableCell className="font-mono font-semibold">{it.reference}</TableCell>
                            <TableCell>{it.designation || "-"}</TableCell>
                            <TableCell className="text-slate-500">{it.vehicle_label || "-"}</TableCell>
                            <TableCell>
                              <Input type="number" min="1" value={it.quantite || 1} className="h-8 w-16"
                                onChange={(e) => updateCartItem(it.id, { quantite: parseInt(e.target.value) || 1 })} />
                            </TableCell>
                            <TableCell>{it.prix || "-"}</TableCell>
                            <TableCell>
                              <Select value={it.statut || "a_commander"} onValueChange={(v) => updateCartItem(it.id, { statut: v })}>
                                <SelectTrigger className="h-8" data-testid={`cart-statut-${it.id}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="a_commander">À commander</SelectItem>
                                  <SelectItem value="commande">Commandé</SelectItem>
                                  <SelectItem value="recu">Reçu</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Button variant="ghost" size="icon" className="text-red-600" onClick={() => deleteCartItem(it.id)} data-testid={`del-cart-${it.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </TabsContent>

        {/* ---------- HISTORIQUE ---------- */}
        <TabsContent value="history" className="space-y-3 mt-4">
          {history.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <History className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>Aucune recherche enregistrée</p>
            </div>
          ) : (
            history.map((h) => (
              <Card key={h.id} className="border-slate-200 hover:border-red-300 cursor-pointer transition-colors" data-testid={`history-${h.id}`}>
                <CardContent className="p-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-mono font-bold text-slate-900">{h.reference}</p>
                    <p className="text-sm text-slate-500 truncate">
                      {h.designation || "—"}{h.vehicle_label ? ` • ${h.vehicle_label}` : ""}
                    </p>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {(h.quotes || []).filter((q) => q.prix).map((q) => (
                        <Badge key={q.supplier_id} variant="outline" className="text-xs">{q.supplier_name}: {q.prix}€</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => { loadSearchIntoTable(h); setActiveTab("compare"); }} data-testid={`reopen-${h.id}`}>
                      Rouvrir
                    </Button>
                    <Button variant="ghost" size="icon" className="text-red-600" onClick={async () => { await api.delete(`/parts/searches/${h.id}`); fetchAll(); }}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Chrome extension dialog */}
      <Dialog open={extDialog} onOpenChange={setExtDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Puzzle className="h-5 w-5" /> Extension Chrome — Capture 1 clic</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1 text-sm">
            <p className="text-slate-600">
              Récupère automatiquement <strong>prix &amp; dispo</strong> depuis la fiche produit d'un fournisseur (où vous êtes déjà connecté) vers le comparateur.
            </p>

            <a href="/carflow-extension.zip" download data-testid="ext-download">
              <Button className="w-full bg-red-600 hover:bg-red-700"><Download className="h-4 w-4 mr-2" /> Télécharger l'extension (.zip)</Button>
            </a>

            <div className="bg-slate-50 border rounded-lg p-3 space-y-2">
              <p className="font-semibold text-slate-800">Installation (une seule fois)</p>
              <ol className="list-decimal list-inside space-y-1 text-slate-600">
                <li>Décompressez le fichier .zip dans un dossier.</li>
                <li>Ouvrez Chrome sur <span className="font-mono bg-white px-1 rounded border">chrome://extensions</span></li>
                <li>Activez le <strong>Mode développeur</strong> (en haut à droite).</li>
                <li>Cliquez <strong>« Charger l'extension non empaquetée »</strong> et sélectionnez le dossier.</li>
                <li>Dans l'extension (⚙️ Réglages), collez l'URL et la clé ci-dessous.</li>
              </ol>
            </div>

            <div className="space-y-2">
              <Label>URL de l'application (à coller dans l'extension)</Label>
              <div className="flex gap-2">
                <Input readOnly value={backendBase} className="font-mono text-xs" data-testid="ext-url" />
                <Button variant="outline" size="icon" onClick={() => copyText(backendBase, "URL")}><Copy className="h-4 w-4" /></Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Clé de capture</Label>
              <div className="flex gap-2">
                <Input readOnly value={captureKey} className="font-mono text-xs" data-testid="ext-key" />
                <Button variant="outline" size="icon" onClick={() => copyText(captureKey, "Clé")}><Copy className="h-4 w-4" /></Button>
                <Button variant="outline" size="icon" onClick={regenerateKey} title="Régénérer"><RefreshCw className="h-4 w-4" /></Button>
              </div>
              <p className="text-xs text-slate-400">Gardez cette clé confidentielle. Régénérer la clé déconnecte l'ancienne extension.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtDialog(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supplier management dialog */}
      <Dialog open={supplierDialog} onOpenChange={setSupplierDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editSupplier ? "Modifier le fournisseur" : "Fournisseurs"}</DialogTitle>
          </DialogHeader>
          {!editSupplier && (
            <div className="space-y-2 max-h-56 overflow-y-auto border rounded-lg p-2">
              {suppliers.map((s) => (
                <div key={s.id} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                    <div className="min-w-0">
                      <p className="font-medium truncate">{s.name}</p>
                      <p className="text-xs text-slate-400 truncate">{s.search_url || "Pas d'URL"}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => openSupplierDialog(s)}>Éditer</Button>
                    <Button variant="ghost" size="icon" className="text-red-600" onClick={() => deleteSupplier(s.id)}><X className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input data-testid="supplier-name" value={supplierForm.name} onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })} placeholder="Autodoc PRO" />
              </div>
              <div className="space-y-2">
                <Label>Couleur</Label>
                <Input type="color" value={supplierForm.color} onChange={(e) => setSupplierForm({ ...supplierForm, color: e.target.value })} className="h-10 w-full" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>URL de recherche <span className="text-slate-400 font-normal">— utilisez {"{ref}"} pour la référence</span></Label>
              <Input data-testid="supplier-url" value={supplierForm.search_url} onChange={(e) => setSupplierForm({ ...supplierForm, search_url: e.target.value })} placeholder="https://www.auto-doc.fr/search?keyword={ref}" className="font-mono text-sm" />
              <p className="text-xs text-slate-400">Laissez vide si le site n'a pas de recherche par URL (login requis).</p>
            </div>
            <div className="space-y-2">
              <Label>Domaine du site <span className="text-slate-400 font-normal">— pour l'extension Chrome</span></Label>
              <Input data-testid="supplier-domain" value={supplierForm.domain} onChange={(e) => setSupplierForm({ ...supplierForm, domain: e.target.value })} placeholder="auto-doc.fr" className="font-mono text-sm" />
              <p className="text-xs text-slate-400">Permet à l'extension de reconnaître automatiquement ce fournisseur.</p>
            </div>
            <div className="space-y-2 w-32">
              <Label>Ordre</Label>
              <Input type="number" value={supplierForm.order} onChange={(e) => setSupplierForm({ ...supplierForm, order: parseInt(e.target.value) || 99 })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSupplierDialog(false)}>Fermer</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={saveSupplier} data-testid="save-supplier-btn">
              {editSupplier ? "Modifier" : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
